import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { rentalService } from '../../services/rentalService';
import type { RentalContract } from '../../types/rental';
import { SelectiveReturnModal } from './SelectiveReturnModal';

// ─── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bgBase: '#faf7ee',
  bgCard: '#ffffff',
  bgElevated: '#f4efe2',
  gold: '#d97706',
  goldLight: '#d97706',
  goldHover: '#b45309',
  textPrimary: '#1c2033',
  textSecondary: '#5b5a52',
  textMuted: '#6b6a5c',
  borderNormal: 'rgba(92,78,51,0.15)',
  borderSubtle: 'rgba(92,78,51,0.25)',
  fontHeading: "'Inter', sans-serif",
  fontLabel: "'Inter', sans-serif",
  fontBody: "'Inter', sans-serif",
};

const thStyle: React.CSSProperties = {
  fontFamily: C.fontLabel,
  fontSize: 9,
  letterSpacing: '0.22em',
  color: C.textMuted,
  textTransform: 'uppercase',
  padding: '10px 16px',
  background: C.bgElevated,
  borderBottom: `1px solid ${C.borderSubtle}`,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  textAlign: 'left',
};

const tdStyle: React.CSSProperties = {
  fontFamily: C.fontBody,
  fontSize: 12,
  color: C.textSecondary,
  padding: '11px 16px',
  borderBottom: `1px solid ${C.borderNormal}`,
  whiteSpace: 'nowrap',
};

const labelStyle: React.CSSProperties = {
  fontFamily: C.fontLabel,
  fontSize: 8,
  letterSpacing: '0.2em',
  color: C.textMuted,
  textTransform: 'uppercase',
  marginBottom: 5,
  display: 'block',
};

const valueStyle: React.CSSProperties = {
  fontFamily: C.fontBody,
  fontSize: 13,
  color: C.textPrimary,
  lineHeight: 1.5,
};

const contractStatusBadge = (status: string): React.CSSProperties => {
  const map: Record<string, { bg: string; color: string; border: string }> = {
    ACTIVE:     { bg: 'rgba(90,201,140,0.15)',  color: '#5ac98c', border: 'rgba(90,201,140,0.3)'  },
    INACTIVE:   { bg: 'rgba(201,140,90,0.15)',  color: '#e8b07a', border: 'rgba(201,140,90,0.3)'  },
    EXPIRED:    { bg: 'rgba(201,90,90,0.15)',   color: '#e87a7a', border: 'rgba(201,90,90,0.3)'   },
    TERMINATED: { bg: 'rgba(120,120,140,0.15)', color: '#a0a0b8', border: 'rgba(120,120,140,0.3)' },
    DRAFT:      { bg: 'rgba(92,78,51,0.22)',  color: '#fbbf24', border: 'rgba(217,119,6,0.25)'  },
  };
  const s = map[status] || map.TERMINATED;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 12px',
    borderRadius: 20,
    fontSize: 10,
    fontFamily: C.fontLabel,
    letterSpacing: '0.18em',
    fontWeight: 600,
    textTransform: 'uppercase',
    background: s.bg,
    color: s.color,
    border: `1px solid ${s.border}`,
  };
};

const machineStatusBadge = (status: string): React.CSSProperties => {
  const map: Record<string, { bg: string; color: string; border: string }> = {
    RENTED:      { bg: 'rgba(90,201,140,0.15)',  color: '#5ac98c', border: 'rgba(90,201,140,0.3)'  },
    RETURNED:    { bg: 'rgba(120,120,140,0.15)', color: '#a0a0b8', border: 'rgba(120,120,140,0.3)' },
    MAINTENANCE: { bg: 'rgba(92,78,51,0.22)',  color: '#fbbf24', border: 'rgba(217,119,6,0.25)'  },
  };
  const s = map[status] || map.RETURNED;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 10px',
    borderRadius: 20,
    fontSize: 10,
    fontFamily: C.fontLabel,
    letterSpacing: '0.14em',
    fontWeight: 600,
    textTransform: 'uppercase',
    background: s.bg,
    color: s.color,
    border: `1px solid ${s.border}`,
  };
};

const refillStatusBadge = (status: string): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 10px',
  borderRadius: 20,
  fontSize: 10,
  fontFamily: C.fontLabel,
  letterSpacing: '0.14em',
  fontWeight: 600,
  textTransform: 'uppercase',
  background: status === 'COMPLETED' ? 'rgba(90,201,140,0.15)' : 'rgba(92,78,51,0.22)',
  color: status === 'COMPLETED' ? '#5ac98c' : '#fbbf24',
  border: `1px solid ${status === 'COMPLETED' ? 'rgba(90,201,140,0.3)' : 'rgba(217,119,6,0.25)'}`,
});

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '9px 12px',
  background: C.bgBase,
  border: `1px solid ${C.borderSubtle}`,
  borderRadius: 4,
  color: C.textPrimary,
  fontFamily: C.fontBody,
  fontSize: 12,
  outline: 'none',
  colorScheme: 'light' as any,
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  cursor: 'pointer',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  resize: 'vertical' as const,
};

export const RentalContractDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showReturnAllModal, setShowReturnAllModal] = useState(false);
  const [showSelectiveReturnModal, setShowSelectiveReturnModal] = useState(false);
  const [hoveredBtn, setHoveredBtn] = useState<string | null>(null);

  console.log('🔍 RentalContractDetail - ID from URL:', id);

  const { data: contract, isLoading, error } = useQuery<RentalContract>({
    queryKey: ['rental-contract', id],
    queryFn: async () => {
      console.log('📡 Fetching contract with ID:', id);
      try {
        const result = await rentalService.getRentalContract(id!);
        console.log('✅ Contract fetched successfully:', result);
        return result;
      } catch (err) {
        console.error('❌ Error fetching contract:', err);
        throw err;
      }
    },
    enabled: !!id
  });

  console.log('📊 Query state:', { isLoading, hasError: !!error, hasContract: !!contract, error });

  const suspendMutation = useMutation({
    mutationFn: () => rentalService.suspendContract(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rental-contract', id] });
      alert('Contract suspended successfully');
    },
    onError: (error: any) => {
      alert(error?.message || 'Failed to suspend contract');
    }
  });

  const returnAllMutation = useMutation({
    mutationFn: (data: {
      returnDate: Date | string;
      returnCondition: string;
      notes?: string;
      setContractStatus?: string;
    }) => rentalService.returnAllMachines(id!, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['rental-contract', id] });
      setShowReturnAllModal(false);
      alert(data.message || 'Machines returned successfully');
    },
    onError: (error: any) => {
      alert(error?.message || 'Failed to return machines');
    }
  });

  const handleSuspendContract = () => {
    if (window.confirm('Are you sure you want to suspend this contract? This will change the status to INACTIVE.')) {
      suspendMutation.mutate();
    }
  };

  // ── Loading ───────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div style={{ padding: '32px 16px', background: C.bgBase, minHeight: '100vh' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ background: C.bgCard, border: `1px solid ${C.borderSubtle}`, borderRadius: 8, padding: 32 }}>
            {[1,2,3,4,5].map(i => (
              <div key={i} style={{ height: i === 1 ? 28 : 16, background: C.bgElevated, borderRadius: 4, marginBottom: 16, width: i === 1 ? '35%' : '100%', opacity: 0.6 }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────────
  if (error || !contract) {
    return (
      <div style={{ padding: '32px 16px', background: C.bgBase, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: C.bgCard, border: `1px solid ${C.borderSubtle}`, borderRadius: 8, padding: 40, textAlign: 'center' }}>
          <p style={{ fontFamily: C.fontBody, color: '#e87a7a', marginBottom: 20 }}>Error loading contract details. Please try again.</p>
          <button
            onClick={() => navigate('/rentals/list')}
            style={{
              fontFamily: C.fontLabel, fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase',
              padding: '10px 24px', borderRadius: 4, border: 'none',
              background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`,
              color: C.bgCard, cursor: 'pointer',
            }}
          >
            Back to List
          </button>
        </div>
      </div>
    );
  }

  const actionBtn = (
    id: string,
    label: string,
    onClick: () => void,
    variant: 'gold' | 'ghost' | 'danger' | 'orange' | 'indigo' | 'link',
    disabled?: boolean,
    isLink?: { to: string }
  ) => {
    const variantStyles: Record<string, React.CSSProperties> = {
      gold:   { background: hoveredBtn === id ? `linear-gradient(135deg, ${C.goldHover}, ${C.gold})` : `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, color: C.bgCard, border: 'none' },
      ghost:  { background: hoveredBtn === id ? C.bgElevated : 'transparent', color: C.textSecondary, border: `1px solid ${C.borderSubtle}` },
      danger: { background: hoveredBtn === id ? C.bgElevated : 'transparent', color: '#e87a7a', border: '1px solid rgba(201,90,90,0.3)' },
      orange: { background: hoveredBtn === id ? C.bgElevated : 'transparent', color: '#e8b07a', border: '1px solid rgba(201,140,90,0.3)' },
      indigo: { background: hoveredBtn === id ? C.bgElevated : 'transparent', color: '#7ab0e8', border: '1px solid rgba(90,140,201,0.3)' },
      link:   { background: hoveredBtn === id ? C.bgElevated : 'transparent', color: C.gold, border: `1px solid ${C.borderSubtle}` },
    };
    const baseStyle: React.CSSProperties = {
      fontFamily: C.fontLabel,
      fontSize: 9,
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
      padding: '9px 20px',
      borderRadius: 4,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      transition: 'all 0.2s',
      whiteSpace: 'nowrap',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      ...variantStyles[variant],
    };

    if (isLink) {
      return (
        <Link key={id} to={isLink.to} style={baseStyle}
          onMouseEnter={() => setHoveredBtn(id)}
          onMouseLeave={() => setHoveredBtn(null)}
        >
          {label}
        </Link>
      );
    }

    return (
      <button
        key={id}
        onClick={onClick}
        disabled={disabled}
        onMouseEnter={() => setHoveredBtn(id)}
        onMouseLeave={() => setHoveredBtn(null)}
        style={baseStyle}
      >
        {label}
      </button>
    );
  };

  return (
    <div style={{ padding: '32px 16px', background: C.bgBase, minHeight: '100vh' }}>
      <div style={{ maxWidth: 1000, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 24 }}>

        {/* ── Page Header ──────────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <button
              onClick={() => navigate('/rentals/list')}
              onMouseEnter={() => setHoveredBtn('back')}
              onMouseLeave={() => setHoveredBtn(null)}
              style={{
                background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
                fontFamily: C.fontBody, fontSize: 12,
                color: hoveredBtn === 'back' ? C.gold : C.textMuted,
                transition: 'color 0.2s', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <svg width={14} height={14} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Contracts
            </button>
            <h1 style={{
              fontFamily: C.fontHeading,
              fontSize: 34,
              fontWeight: 300,
              color: C.textPrimary,
              letterSpacing: '0.04em',
              margin: 0,
              lineHeight: 1.1,
            }}>
              Contract <span style={{ color: C.gold }}>{contract.contractNumber}</span>
            </h1>
            <div style={{ width: 56, height: 1, background: `linear-gradient(to right, ${C.gold}, transparent)`, marginTop: 8 }} />
          </div>
          <span style={contractStatusBadge(contract.status)}>{contract.status}</span>
        </div>

        {/* ── Contract Details ─────────────────────────────────────────────────── */}
        <div style={{ background: C.bgCard, border: `1px solid ${C.borderNormal}`, borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ padding: '16px 24px', borderBottom: `1px solid ${C.borderSubtle}`, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 3, height: 16, background: C.gold, borderRadius: 2 }} />
            <h2 style={{ fontFamily: C.fontHeading, fontSize: 20, fontWeight: 300, color: C.textPrimary, margin: 0 }}>Contract Details</h2>
          </div>
          <div style={{ padding: 24 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
              <div>
                <span style={labelStyle}>Customer</span>
                <p style={{ ...valueStyle, margin: 0 }}>{contract.customer?.name || 'N/A'}</p>
                {contract.customer?.email && (
                  <p style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textMuted, margin: '4px 0 0 0' }}>{contract.customer.email}</p>
                )}
              </div>

              <div>
                <span style={labelStyle}>Contract Period</span>
                <p style={{ ...valueStyle, margin: 0 }}>
                  {new Date(contract.startDate).toLocaleDateString()} — {new Date(contract.endDate).toLocaleDateString()}
                </p>
                <p style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textMuted, margin: '4px 0 0 0' }}>
                  {Math.ceil((new Date(contract.endDate).getTime() - new Date(contract.startDate).getTime()) / (1000 * 60 * 60 * 24))} days
                </p>
              </div>

              <div>
                <span style={labelStyle}>Monthly Rental Fee</span>
                <p style={{ fontFamily: C.fontHeading, fontSize: 26, fontWeight: 300, color: C.goldLight, margin: 0, letterSpacing: '0.02em' }}>
                  BD {contract.monthlyRentalFee?.toFixed(2) || '0.00'}
                </p>
              </div>

              <div>
                <span style={labelStyle}>Refill Service Fee</span>
                <p style={{ ...valueStyle, margin: 0 }}>BD {contract.refillServiceFee?.toFixed(2) || '0.00'}</p>
              </div>

              <div>
                <span style={labelStyle}>Refill Interval</span>
                <p style={{ ...valueStyle, margin: 0 }}>Every {contract.refillIntervalDays || 30} days</p>
              </div>

              <div>
                <span style={labelStyle}>Auto Renew</span>
                <p style={{ ...valueStyle, margin: 0 }}>{contract.autoRenew ? 'Yes' : 'No'}</p>
              </div>

              {contract.notes && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <span style={labelStyle}>Notes</span>
                  <p style={{ ...valueStyle, margin: 0, whiteSpace: 'pre-wrap' }}>{contract.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Machines ─────────────────────────────────────────────────────────── */}
        <div style={{ background: C.bgCard, border: `1px solid ${C.borderNormal}`, borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ padding: '16px 24px', borderBottom: `1px solid ${C.borderSubtle}`, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 3, height: 16, background: C.gold, borderRadius: 2 }} />
            <h2 style={{ fontFamily: C.fontHeading, fontSize: 20, fontWeight: 300, color: C.textPrimary, margin: 0 }}>
              Rented Machines
              <span style={{ fontFamily: C.fontBody, fontSize: 13, color: C.textMuted, marginLeft: 10 }}>({contract.machines?.length || 0})</span>
            </h2>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Serial Number</th>
                  <th style={thStyle}>Product</th>
                  <th style={thStyle}>Monthly Fee</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Rent Start</th>
                  <th style={thStyle}>Return Date</th>
                </tr>
              </thead>
              <tbody>
                {contract.machines?.map((machine: any) => (
                  <tr key={machine.id}>
                    <td style={{ ...tdStyle, color: C.textPrimary }}>{machine.serialNumber?.serialNumber || 'N/A'}</td>
                    <td style={tdStyle}>{machine.serialNumber?.product?.name || 'N/A'}</td>
                    <td style={{ ...tdStyle, color: C.textPrimary }}>BD {machine.monthlyRentalFee?.toFixed(2) || '0.00'}</td>
                    <td style={tdStyle}>
                      <span style={machineStatusBadge(machine.status)}>{machine.status}</span>
                    </td>
                    <td style={tdStyle}>
                      {machine.rentStartDate ? new Date(machine.rentStartDate).toLocaleDateString() : 'N/A'}
                    </td>
                    <td style={tdStyle}>
                      {machine.returnDate ? new Date(machine.returnDate).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Refill History ───────────────────────────────────────────────────── */}
        {contract.refillVisits && contract.refillVisits.length > 0 && (
          <div style={{ background: C.bgCard, border: `1px solid ${C.borderNormal}`, borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ padding: '16px 24px', borderBottom: `1px solid ${C.borderSubtle}`, display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 3, height: 16, background: C.gold, borderRadius: 2 }} />
              <h2 style={{ fontFamily: C.fontHeading, fontSize: 20, fontWeight: 300, color: C.textPrimary, margin: 0 }}>
                Refill History
                <span style={{ fontFamily: C.fontBody, fontSize: 13, color: C.textMuted, marginLeft: 10 }}>({contract.refillVisits.length})</span>
              </h2>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Employee</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {contract.refillVisits.map((visit: any) => (
                    <tr key={visit.id}>
                      <td style={tdStyle}>{new Date(visit.visitDate).toLocaleDateString()}</td>
                      <td style={{ ...tdStyle, color: C.textPrimary }}>
                        {visit.employee?.firstName || 'N/A'} {visit.employee?.lastName || ''}
                      </td>
                      <td style={tdStyle}>
                        <span style={refillStatusBadge(visit.status)}>{visit.status}</span>
                      </td>
                      <td style={tdStyle}>{visit.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Actions ──────────────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
          {actionBtn('back-list', 'Back to List', () => navigate('/rentals/list'), 'ghost')}

          {contract.status === 'ACTIVE' && (
            <>
              {actionBtn(
                'suspend',
                suspendMutation.isPending ? 'Suspending...' : 'Suspend Contract',
                handleSuspendContract,
                'orange',
                suspendMutation.isPending
              )}
              {actionBtn('record-refill', 'Record Refill', () => {}, 'indigo', false, { to: `/rentals/refill?contractId=${contract.id}` })}
            </>
          )}

          {(contract.status === 'ACTIVE' || contract.status === 'INACTIVE') && (
            <>
              {actionBtn('selective-return', 'Manage Machine Returns', () => setShowSelectiveReturnModal(true), 'indigo')}
              {actionBtn('return-all', 'Return All Machines', () => setShowReturnAllModal(true), 'danger')}
            </>
          )}
        </div>

        {/* ── Return All Machines Modal ─────────────────────────────────────────── */}
        {showReturnAllModal && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(7,7,15,0.85)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}>
            <div style={{
              background: C.bgCard, border: `1px solid ${C.borderSubtle}`, borderRadius: 8,
              padding: 32, maxWidth: 480, width: '100%',
            }}>
              <h3 style={{ fontFamily: C.fontHeading, fontSize: 26, fontWeight: 300, color: C.textPrimary, margin: '0 0 8px 0', letterSpacing: '0.03em' }}>
                Return All Machines
              </h3>
              <div style={{ width: 36, height: 1, background: `linear-gradient(to right, ${C.gold}, transparent)`, marginBottom: 24 }} />

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.currentTarget);
                  returnAllMutation.mutate({
                    returnDate: formData.get('returnDate') as string,
                    returnCondition: formData.get('returnCondition') as string,
                    notes: formData.get('notes') as string || undefined,
                    setContractStatus: formData.get('setContractStatus') as string
                  });
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  <div>
                    <label style={labelStyle}>Return Date</label>
                    <input
                      type="date"
                      name="returnDate"
                      required
                      defaultValue={new Date().toISOString().split('T')[0]}
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Condition</label>
                    <select name="returnCondition" required style={selectStyle}>
                      <option value="EXCELLENT">Excellent</option>
                      <option value="GOOD">Good</option>
                      <option value="FAIR">Fair</option>
                      <option value="POOR">Poor</option>
                      <option value="DAMAGED">Damaged</option>
                    </select>
                  </div>

                  <div>
                    <label style={labelStyle}>Contract Status After Return</label>
                    <select name="setContractStatus" required style={selectStyle}>
                      <option value="INACTIVE">Inactive (Keep contract, suspend service)</option>
                      <option value="TERMINATED">Terminated (End contract)</option>
                    </select>
                  </div>

                  <div>
                    <label style={labelStyle}>Notes (Optional)</label>
                    <textarea
                      name="notes"
                      rows={3}
                      style={textareaStyle}
                      placeholder="Reason for return, machine condition notes, etc."
                    />
                  </div>

                  <div style={{
                    background: 'rgba(239,232,213,0.6)',
                    border: `1px solid ${C.borderNormal}`,
                    borderLeft: `3px solid ${C.gold}`,
                    borderRadius: 4,
                    padding: '10px 14px',
                  }}>
                    <p style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textSecondary, margin: 0 }}>
                      This will return <strong style={{ color: C.textPrimary }}>{contract.machines?.filter(m => m.status === 'RENTED').length || 0} machine(s)</strong> and update their serial numbers to IN_STOCK status.
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
                  <button
                    type="button"
                    onClick={() => setShowReturnAllModal(false)}
                    onMouseEnter={() => setHoveredBtn('modal-cancel')}
                    onMouseLeave={() => setHoveredBtn(null)}
                    style={{
                      fontFamily: C.fontLabel, fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase',
                      padding: '9px 20px', borderRadius: 4, cursor: 'pointer',
                      background: hoveredBtn === 'modal-cancel' ? C.bgElevated : 'transparent',
                      color: C.textSecondary, border: `1px solid ${C.borderSubtle}`,
                      transition: 'all 0.2s',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={returnAllMutation.isPending}
                    onMouseEnter={() => setHoveredBtn('modal-submit')}
                    onMouseLeave={() => setHoveredBtn(null)}
                    style={{
                      fontFamily: C.fontLabel, fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase',
                      padding: '9px 20px', borderRadius: 4, cursor: returnAllMutation.isPending ? 'not-allowed' : 'pointer',
                      background: hoveredBtn === 'modal-submit' && !returnAllMutation.isPending
                        ? C.bgElevated
                        : 'transparent',
                      color: '#e87a7a',
                      border: '1px solid rgba(201,90,90,0.3)',
                      opacity: returnAllMutation.isPending ? 0.5 : 1,
                      transition: 'all 0.2s',
                    }}
                  >
                    {returnAllMutation.isPending ? 'Returning...' : 'Return All Machines'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Selective Return Modal */}
        {showSelectiveReturnModal && contract && (
          <SelectiveReturnModal
            isOpen={showSelectiveReturnModal}
            onClose={() => setShowSelectiveReturnModal(false)}
            contract={contract}
          />
        )}
      </div>
    </div>
  );
};
